import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import MUIChip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { formatDate } from 'utils';
import React from 'react';
import { WhiteBox } from 'components/AppShell'
import Chip from 'components/Chip';
import { CopyButton } from 'components/CustomButtons';
import { Skeleton } from '@mui/material';

// Helper function for singular/plural text
const getStudentText = (count) => {
  return count === 1 ? 'student' : 'students';
};

function TemplateInfo({ template, interviewDetails={}, loading=false, error=null, interviewType='Assignment' }) {
  // Use interview details data if available, otherwise fallback to template data
  const details = Object.keys(interviewDetails).length > 0 ? interviewDetails : template;
  
  const averageTime = details?.averageTime || details?.metadata?.averageTime || 0;
  const mins = Math.floor(averageTime / 60);
  const secs = Math.round(averageTime % 60);
  let atText = 'N/A';
  if (mins) atText = `${mins} min`;
  if (secs) atText = `${mins > 0 ? mins + ' min ' : ''}${secs} secs`;

  const expiresOn = details?.settings?.expiresOn || details?.expiresOn;
  const expiresDate = expiresOn && dayjs(expiresOn).year() !== 9999
    ? formatDate(expiresOn, 'DD MMM YYYY, (dddd)')
    : 'Never expires';
  const expiresIsFuture = expiresOn && dayjs(expiresOn).isAfter(dayjs()) && dayjs(expiresOn).year() !== 9999;

  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(details?._id || template?._id || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  // Calculate attempted by count
  const attemptedByCount = details?.attemptedBy?.length || details?.attemptedBy || details?.totalAttemptsCount || template?.metadata?.usedBy?.toString() || template?.totalAttemptsCount || 0;


  if (error) {
    return (
      <WhiteBox>
        <Typography color="error">Error loading interview details: {error}</Typography>
      </WhiteBox>
    );
  }

  return (
    <WhiteBox>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="h4-medium">
            {loading ? <Skeleton variant="text" width={300} height={40} /> : (details?.settings?.name || details?.name || template?.name)}
          </Typography>
          <Chip
            content={<Typography variant="body01-bold">{interviewType}</Typography>}
            bgColor="#E5F1FF"
            color="#003E8C"
            sx={{borderRadius: 0}}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1} border="1px solid #CCD4DE" borderRadius={1} p={1}>
          <Typography variant="body01-bold" color="neutral.clr-900">
            Assessment ID:
          </Typography>
          <Typography variant="body01-bold" color="neutral.clr-600">
            {loading ? <Skeleton variant="text" width={150} height={20} /> : (details?._id || template?._id)}
          </Typography>
          <CopyButton
            content={details?._id || template?._id}
            title="copy"
            iconProps={{ color: "primary", fontSize: '10px !important' }}
            size="small"
          />
        </Box>
      </Box>
      <Divider sx={{ my: 2, borderColor: '#E3E8EF' }} />
      {/* Details */}
      <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Typography variant="h6-medium" color="neutral.clr-900">
            Batch: &nbsp;
            <Typography variant="h6-medium" color="neutral.clr-600">
              {loading ? <Skeleton variant="text" width={120} height={20} /> : (details?.batch?.name || template?.batch?.name || 'N/A')}
            </Typography>
          </Typography>
          <Typography variant="h6-medium" color="neutral.clr-900">
            Attempted by: &nbsp;
            <Typography variant="h6-medium" color="neutral.clr-600">
              {loading ? <Skeleton variant="text" width={100} height={20} /> : `${attemptedByCount} ${getStudentText(attemptedByCount)}`}
            </Typography>
          </Typography>
          <Typography variant="h6-medium" color="neutral.clr-900">
            Average Attempt Time: &nbsp;
            <Typography variant="h6-medium" color="neutral.clr-600">
              {loading ? <Skeleton variant="text" width={80} height={20} /> : atText}
            </Typography>
          </Typography>
          <Typography variant="h6-medium" color="neutral.clr-900">
            Difficulty: &nbsp;
            <Typography variant="h6-medium" color="neutral.clr-600">
              {loading ? <Skeleton variant="text" width={60} height={20} /> : (details?.details?.challenges?.[0]?.difficulty || details?.difficulty || template?.difficulty || 'N/A')}
            </Typography>
          </Typography>
          <Typography variant="h6-medium" color="neutral.clr-900">
            Domain: &nbsp;
            <Typography variant="h6-medium" color="neutral.clr-600">
              {loading ? <Skeleton variant="text" width={100} height={20} /> : (details?.details?.company?.name || details?.domain?.name || template?.domain?.name || 'N/A')}
            </Typography>
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" gap={1.5} alignItems="flex-end" minWidth={220}>
          <Typography variant="h6-medium" color="neutral.clr-900">
            Published on: &nbsp;
            <Typography variant="h6-medium" color="neutral.clr-600">
              {loading ? <Skeleton variant="text" width={180} height={20} /> : ((details?.settings?.publishOn || details?.publishOn || template?.publishOn) ? formatDate(details?.settings?.publishOn || details?.publishOn || template?.publishOn, 'DD MMM YYYY, (dddd)') : 'N/A')}
            </Typography>
          </Typography>
          <Typography variant="h6-medium" color={expiresIsFuture ? 'error.main' : 'neutral.clr-900'}>
            {loading ? (
              <>
                Expires on: &nbsp;<Skeleton variant="text" width={180} height={20} sx={{display: 'inline-block'}} />
              </>
            ) : expiresIsFuture ? (
              <>
                Expires on: &nbsp;{expiresDate}
              </>
            ) : (
              <>
                Expires on: &nbsp;
                <Typography variant="h6-medium" color={expiresDate === 'Never expires' ? 'error.main' : 'neutral.clr-600'}>
                  {expiresDate}
                </Typography>
              </>
            )}
          </Typography>
        </Box>
      </Box>
    </WhiteBox>
  );
}

export default TemplateInfo;